<template>
    <b-card-code v-if="!error_loading"
      :title='$t(`message.sales`)'
      no-body
    >
    
      <b-card-body>
        <div class="d-flex justify-content-between flex-wrap">
  
          <!-- sorting  -->
          <b-form-group
            label="Sort"
            label-size="sm"
            label-align-sm="left"
            label-cols-sm="2"
            label-for="sortBySelect"
            class="mr-1 mb-md-0 align-items-center"
          >
            <b-input-group
              size="sm"
            >
              <b-form-select
                id="sortBySelect"
                v-model="sortBy"
                :options="sortOptions"
              >
                <template #first>
                  <option value="">
                    none
                  </option>
                </template>
              </b-form-select>
              <b-form-select
                v-model="sortDesc"
                size="sm"
                :disabled="!sortBy"
              >
                <option :value="false">
                  Asc
                </option>
                <option :value="true">
                  Desc
                </option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
  
          <!-- filter -->
          <b-form-group
            label="Filter"
            label-cols-sm="2"
            label-align-sm="left"
            label-size="sm"
            label-for="filterInput"
            class="mb-0 align-items-center"
          >
            <b-input-group size="sm">
              <b-form-input
                id="filterInput"
                v-model="filter"
                type="search"
                placeholder="Type to Search"
                debounce=500
              />
              <b-input-group-append>
                <b-button
                  :disabled="!filter"
                  @click="filter = ''"
                >
                  Clear
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </div>
      </b-card-body>
  
  
    <b-card-code
      class="card_code_table"
      no-body
    >
      <b-skeleton-table
        v-if="isBusy"
        :rows="5"
        :columns="5"
        :table-props="{ bordered: true, striped: true }"
      ></b-skeleton-table>
  
      <b-table
        v-else
        ref="selectableTable"
        striped
        responsive
        :per-page="perPage"
        :current-page="currentPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        :filter="filter"
        :filter-included-fields="filterOn"
        @filtered="onFiltered"
        :busy="isBusy"
        >
        <!-- state -->
        <template #cell(state)="data">
            <b-dropdown v-if="ls_role=='admin'" :variant='`${data.item.state=="issued" ? "outline-success" : "outline-danger"}`' size="sm" :id="'dropdown-st'+data.item.transaction_id" :text="data.item.state" class="m-md-2">
              <b-dropdown-item variant="success" @click="updateState(data.item.transaction_id,'issued')">issued</b-dropdown-item>
              <b-dropdown-item variant="danger" @click="updateState(data.item.transaction_id,'cancelled')">cancelled</b-dropdown-item>
          </b-dropdown>
          <b-badge v-else pill :class="data.item.state">
                {{ data.item.state }} 
            </b-badge>
        </template>
        <!-- customer_name -->
        <template #cell(customer_name)="data">
            <b-dropdown block variant="outline-success" size="sm" :id="'dropdown-csn'+data.item.transaction_id" :text="data.item.customer_name" class="m-md-2" @show="sendInfo(data.item.transaction_id, 'information')">
              <b-dropdown-item variant="success" v-if="!customer_link">{{ loadingDrop }}</b-dropdown-item>
              <b-dropdown-item variant="success" v-else target="_blank" :href="customer_link">{{ data.item.customer_name }} Info</b-dropdown-item>
          </b-dropdown>
        </template>
        <!-- summary -->
        <template #cell(summary)="data">
            <b-dropdown block variant="outline-secondary" size="sm" :id="'dropdown-dn'+data.item.transaction_id" text="Debit Note" class="m-md-2" @show="sendInfo(data.item.transaction_id, 'summary')">
              <b-dropdown-item variant="secondary" v-if="!debit_link">{{ loadingDrop }}</b-dropdown-item>
              <b-dropdown-item variant="secondary" v-else target="_blank" :href="debit_link">Summary</b-dropdown-item>
          </b-dropdown>
        </template>
        <!-- customers -->
        <template #cell(insurance_evidence)="data">
            <b-dropdown size="sm" :id="'dropdown-ins'+data.item.quote_id" text="Download" class="m-md-2" @show="getCustomers(data.item.transaction_id, data.item.policyType, data.item.summary)" boundary="viewport">
                <b-dropdown-item v-if="!customersArr.length">{{ loadingDrop }}</b-dropdown-item>
                <b-dropdown-item v-else target="_blank" :href="policyLink">Insurance Evidence</b-dropdown-item>
                <b-dropdown-item v-for="(item, index) in customersArr" :key="index" target="_blank" :href="policyLink+'&tr='+index">{{ item }}</b-dropdown-item>
            </b-dropdown>
        </template>
        <!-- validation certificate -->
        <template #cell(validation_certificate)="data">
            <b-button v-if="checkValidation(data.item.policyType)" @click="validCert(data.item.policyType, data.item.summary)" size="sm" variant="outline-secondary">Download</b-button>
            <p v-else class="text-center">&#9473;</p>
        </template>
        <!-- master policy -->
        <template #cell(master_policy)="data">
          <div v-if="isSompoProduct(data.item.policyType)" class="dropdown">
            <b-dropdown :id="'dropdown-sompo' + '-' + data.item.transaction_id" size="sm" variant="outline-secondary">
              <template #button-content>
                <font-awesome-icon :icon="['fas', 'download']" />
              </template>
              <b-dropdown-item target="_blank" :href="product_documents[data.item.policyType].pwo_link">
                Policy Wording (english)
              </b-dropdown-item>
              <b-dropdown-item target="_blank" :href="product_documents[data.item.policyType].pwo_link.replace('.pdf', '_EL.pdf')">
                Policy Wording (greek)
              </b-dropdown-item>
              <b-dropdown-item v-if="data.item.policyType=='product-longstay-302'" target="_blank" :href="product_documents[data.item.policyType].pwo_link_addition">Addition to the Policy Wording (english)</b-dropdown-item>
            </b-dropdown>
          </div>
          <b-button
            v-else-if="checkPolicy(data.item.policyType)"
            @click="masterPolicies(data.item.policyType)"
            size="sm"
            variant="outline-secondary"
          >
            <font-awesome-icon :icon="['fas', 'download']" />
          </b-button>
          <p v-else class="text-center">&#9473;</p>
        </template>
        <!-- tob -->
        <template #cell(tob)="data">
            <b-button v-if="checkTobs(data.item.policyType)" @click="tobs(data.item.policyType)" size="sm" variant="outline-secondary"><font-awesome-icon :icon="['fas', 'download']" /></b-button>
            <p v-else class="text-center">&#9473;</p>
        </template>
        <!-- ipid -->
        <template #cell(ipid)="data">
            <b-button v-if="checkIpids(data.item.policyType)" @click="ipids(data.item.policyType)" size="sm" variant="outline-secondary"><font-awesome-icon :icon="['fas', 'download']" /></b-button>
            <p v-else class="text-center">&#9473;</p>
        </template>
        <!-- email -->
        <template #cell(email_schedule)="data">
            <font-awesome-icon icon="fas fa-envelope" @click="sendEmail(data.item.policyType, data.item.summary)" style="cursor:pointer;" title="Send email with insurance evidence"/>
        </template>
      </b-table>
  
      <!-- buttons -->
      <b-card-body class="demo-inline-spacing pt-0" v-if="items.length">
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary"
          @click="downloadExcel(items,'Sales.xlsx')"
        >
          {{$t('message.sales')}} Excel
        </b-button>
      </b-card-body>
  
      <template #code>
        {{ codeRowSelectSupport }}
      </template>
    </b-card-code>
  
      <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
  
        <!-- page length -->
        <b-form-group
          label="Per Page"
          label-cols="6"
          label-align="left"
          label-size="sm"
          label-for="sortBySelect"
          class="text-nowrap mb-md-0 mr-1 align-items-center"
        >
          <b-form-select
            id="perPageSelect"
            v-model="perPage"
            size="sm"
            inline
            :options="pageOptions"
          />
        </b-form-group>
  
        <!-- pagination -->
        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0 align-items-center"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </div>
      </b-card-body>
  
      <template #code>
        {{ codeKitchenSink }}
      </template>
    </b-card-code>
    <ErrorAlert 
      v-else
      :heading="$t('message.couldntloadsalesdts')"
      :message="$t('message.couldntloadsalesdtsmsg')"
    />
  </template>
  
  <script>
  import { product_documents, downloadExcelMixin, validationProducts, product_certificates, policy_link_map, special_4_products, APP_TOKENS } from '@/products';
  import ErrorAlert from '../pages/components/ErrorAlert.vue';
  import BCardCode from '@core/components/b-card-code/BCardCode.vue'
  import {
    BTable, BFormGroup, BFormSelect, BPagination, BInputGroup, BFormInput, BInputGroupAppend, BButton, BCardBody, BDropdown, BDropdownItem, BSkeletonTable, BBadge
  } from 'bootstrap-vue'
  import { codeKitchenSink } from '../table/bs-table/code'
  import Ripple from 'vue-ripple-directive'
  import { codeRowSelectSupport } from '../table/bs-table/code'
  import { performLogoutWithoutRedirect } from '@/auth/logout'
  
  export default {
    mixins: [downloadExcelMixin],
    components: {
      ErrorAlert,
      BCardCode,
      BTable,
      BFormGroup,
      BFormSelect,
      BPagination,
      BInputGroup,
      BFormInput,
      BInputGroupAppend,
      BButton,
      BCardBody,
      BDropdown,
      BDropdownItem,
      BSkeletonTable,
      BBadge
    },
    directives: {
      Ripple,
    },
    data() {
      return {
        error_loading: false,
        customer_link: null,
        debit_link: null,
        processing: false,
        loadingDrop: 'Loading...',
        isBusy: true,
        policyLink:'',
        masterPolicy:null,
        validCerf:null,
        tobLink:null,
        ipidLink:null,
        customersArr: [],
        perPage: 5,
        pageOptions: [5, 10, 25, 50, 100],
        totalRows: 1,
        currentPage: 1,
        sortBy: 'r_timestamp',
        sortDesc: true,
        sortDirection: 'desc',
        filter: null,
        filterOn: [],
        fields: [
          { key: 'quote_id', label: 'ID', sortable: true },
          { key: 'transaction_id', label: 'Transaction ID', sortable: true },
          { key: 'state', label: 'State', sortable: true },
          { key: 'number_of_customers', label: 'No. of Insured', sortable: true },
          { key: 'customer_name', label: 'Persons', sortable: true },
          { key: 'email', label: 'Email', sortable: true },
          { key: 'on_behalf_email', label: 'Behalf Of', sortable: true },
          { key: 'residence_literal', label: 'Residence', sortable: true, tdClass: 'text-nowrap' },
          { key: 'destination_literal', label: 'Destination', sortable: true, tdClass: 'text-nowrap' },
          { key: 'zone', label: 'Zone', sortable: true, tdClass: 'text-nowrap' },
          { key: 'start_date', label: 'Start Date', sortable: true },
          { key: 'end_date', label: 'End Date', sortable: true },
          { key: 'product_name', label: 'Product', sortable: true, tdClass: 'text-nowrap' },
          { key: 'full_amount', label: 'Total Amount', sortable: true },
          { key: 'clean_amount', label: 'Clean Amount', sortable: true },
          { key: 'consent', label: 'Consent', sortable: true },        
          { key: 'summary', label: 'Summary' },
          { key: 'insurance_evidence', label: 'Insurance Evidence' },
          { key: 'validation_certificate', label: 'Validation Certificate' },
          { key: 'master_policy', label: 'Master Policy' },
          { key: 'tob', label: 'TOB' },
          { key: 'ipid', label: 'IPID'},
          { key: 'email_schedule', label: 'Email Schedule' },
          { key: 'platform', label: 'Platform' },
          { key: 'r_timestamp', label: 'Date of Issue', sortable: true, tdClass: 'text-nowrap' },
        ],
        /* eslint-disable global-require */
        items: [],
        codeKitchenSink,
        codeRowSelectSupport,
        product_documents
      }
    },
    computed: {
      cookie() {
        return window?.$cookies?.get("jwt") ?? null;
      },
      token_id() {
        return this.$store.state.app?.userData?.id ?? null
      },
      ls_email() {
        return this.$store.state.app?.userData?.email ?? ''
      },
      ls_role() {
        return this.$store.state.app?.userData?.role ?? ''
      },
      sortOptions() {
        // Create an options list from our fields
        return this.fields
          .filter(f => f.sortable)
          .map(f => ({ text: f.label, value: f.key }))
      },
    },
    async created() {
      // Fetch the first batch and display results
      await this.fetchTable(0, 50, true);
      // Define the ranges for subsequent fetches
      const ranges = [
          [50, 150],
          [200, 300],
          [500, 1000],
          [1500, 2000]
      ];
      // Fetch remaining batches sequentially using promise chaining
      for (const [start, end] of ranges) {
          await this.fetchTable(start, end, false);
      }
    },
    methods: {
      checkValidation(product) {
        return validationProducts.includes(product);
      },
      checkIpids(product) {
        const num = product.replace(/^\D+/g, '');
        if (num[0] == '8') {
            return false
        } else if (num[0] == '4') {
            if (['product-single-408','product-single-409','product-single-410'].includes(product)) {
              return false
            }
        }
        return true
      },
      checkTobs(product) {
        const num = product.replace(/^\D+/g, '');
        if (num[0] == '8' || num[0] == '4') {
            return false
        } 
        return true
      },
      checkPolicy(product) {
        const num = product.replace(/^\D+/g, '');
        if (num[0] == '6') {
            return false
        } 
        return true
      },
      isSompoProduct(product) {
        return (
        product.startsWith('product-single-3') ||
        product.startsWith('product-longstay-3') ||
        product.startsWith('product-annual-3')
        );
      },
      async fetchTable(start_limit, end_limit, init) {
        const url= {
          prod: 'https://api-quote.safetravel.today/v2/app/sales'
        };
        const data = {
          token: this.token_id,
          jwt: this.cookie,
          start_limit: start_limit,
          end_limit: end_limit,
        }
        try {
          await fetch(url.prod, {
              method: 'post',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(data)
          })
          .then((response) => {
            if (!response.ok) throw new Error(`Failed to fetch sales data: HTTP ${response.status}`);
            return response.json();
          })
          .then((res)=>{
            if (res === 'not_valid') {
              performLogoutWithoutRedirect(this.$store)
              return;
            }
            if (res) {
              res.forEach((r)=> {
                if (!this.items.some(item => item.transaction_id === r.transaction_id)) {
                  this.items.push({
                    quote_id: r?.quote_id,
                    transaction_id: r?.transaction_id,
                    state: r?.state,
                    number_of_customers: r?.number_of_customers,
                    customer_name: r?.customer_name,
                    residence_literal: r?.residence_literal,
                    destination_literal: r?.destination_literal,
                    email: r?.email,
                    on_behalf_email: r?.on_behalf_email,
                    zone: r?.zone,
                    start_date: r?.start_date,
                    end_date: r?.end_date,
                    product_name: r?.product_name,
                    full_amount: r?.full_amount,
                    clean_amount: r?.clean_amount,
                    consent: r?.consent==0 ? 'No' : 'Yes',
                    summary: r?.transaction_token,
                    insurance_evidence: '',
                    validation_certificate: '',
                    master_policy: '',
                    ipid: '',
                    tob: '',
                    email_schedule: r?.email_schedule,
                    r_timestamp: r?.r_timestamp,
                    platform: APP_TOKENS.includes(r?.on_behalf_of) ? 'app' : 'erp',
                    policyType: r?.policyType,
                    on_behalf_of: r?.on_behalf_of
                  })
                }
              });
            }
          })
          .then(() => {
            this.totalRows = this.items.length;
            this.isBusy = false;
            this.sortBy = 'r_timestamp';
          })
        } catch (error) {
          // TypeError: Failed to fetch
          console.warn('There was an error', error);
          if (init) this.error_loading = true;
        }
      },
      onFiltered(filteredItems) {
        // Trigger pagination to update the number of buttons/pages due to filtering
        this.totalRows = filteredItems.length
        this.currentPage = 1
      },
      async getCustomers(tr_id, policyType, tr_token) {
        this.customersArr=[];
        const url= {
            prod: 'https://api-quote.safetravel.today/v2/app/customer'
        };
        const data = {
            token: this.token_id,
            transactionID: tr_id,
            jwt: this.cookie
        }
        try {
            this.loadingDrop='Loading...';
            await fetch(url.prod, {
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            })
            .then((response) => {
              if (!response.ok) throw new Error(`Failed to fetch customer: HTTP ${response.status}`);
              return response.json();
            })
            .then((response)=> {
              if (response === 'not_valid') {
                performLogoutWithoutRedirect(this.$store)
                return;
              }
              if (response) {
                response.forEach(element => {
                    this.customersArr.push(element.customer_name);
                    this.customersArr= [...new Set(this.customersArr)];
                });
              }
            })
            .then(()=> {
              this.policyLinks(policyType, tr_token);
            })
        } catch (error) {
            // TypeError: Failed to fetch
            console.warn('There was an error fetching customers', error);
            this.loadingDrop='Error...';
        }
      },
      policyLinks(policyType, tr_token) {
        const num = policyType.replace(/^\D+/g, '');
        let prefix = num[0];

        if (prefix === '4') {
          prefix = special_4_products.includes(policyType) ? '4a' : '4b';
        }
        this.policyLink = policy_link_map[prefix] ? policy_link_map[prefix] + tr_token : '';
      },
      validCert(product, tr_token) {
        let certificateUrl = null;
        
        if (product_certificates.HANNOVER.includes(product)) {
          certificateUrl = `https://quote.safetravel.today/validation_certificate.php?token=${tr_token}`;
        } else if (product_certificates.SOMPO.includes(product)) {
          certificateUrl = `https://quote.safetravel.today/validation_certificate_s.php?token=${tr_token}`;
        }

        if (certificateUrl) {
          window.open(certificateUrl);
        }
      },
      masterPolicies(product_code) {
        this.masterPolicy= this.product_documents[product_code]?.pwo_link;
        // open master policy
        if (this.masterPolicy) {
            window.open(this.masterPolicy);
        }
      },
      tobs(product_code) {
        this.tobLink=this.product_documents[product_code]?.tob_link;
        // open tob 
        if (this.tobLink) {
            window.open(this.tobLink);
        }
      },
      ipids(product_code) {
        this.ipidLink = this.product_documents[product_code]?.ipid_link;
        // open ipid 
        if (this.ipidLink) {
            window.open(this.ipidLink);
        }
      },
      sendEmail(policyType, tr_token) {
        this.policyLinks(policyType, tr_token);
        if (this.policyLink) {
            window.location.href = `mailto:${this.ls_email}?subject=Insurance Evidence&body=Here is the link to the insurance evidence: ${this.policyLink}`;
        }
      },
      updateState(transactionID,state) {
        const url= {
                prod: 'https://api-quote.safetravel.today/v2/app/change_state'
            };
            const data = {
                token: this.token_id,
                jwt: this.cookie,
                transactionID,
                state: state.toLowerCase()
            }
            try {
                fetch(url.prod, {
                    method: 'post',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data)
                })
                .then((response) => {
                  if (!response.ok) throw new Error(`Failed to change state: HTTP ${response.status}`);
                  return response.json();
                })
                .then((res)=> {
                  if (res) {
                    const itemIndex = this.items.findIndex(item => item.transaction_id === transactionID);
                    if (itemIndex !== -1) {
                      this.$set(this.items, itemIndex, {
                        ...this.items[itemIndex],  // keep existing props
                        state: state               // update state prop
                      });
                    }
                  } else {
                    alert('There was an error changing state');
                  }
                })
            } catch (error) {
                // TypeError: Failed to fetch
                console.warn('There was an error changing state', error);
            }
      },
      async sendInfo(policyId, pageLink) {
        this.customer_link=null;
        this.debit_link=null;
        // if a request is processing
        if (this.processing === true) {
          return;
        } 
        this.processing = true;

        const url= {
            prod: 'https://api-quote.safetravel.today/v2/app/get_tr_tokenized'
        };
        const data = {
            token: this.token_id,
            policyID: policyId,
            jwt: this.cookie
        };
        try {
          await fetch(url.prod, {
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            })
            .then((response) => {
                if (!response.ok) throw new Error(`Failed to get tokenized link: HTTP ${response.status}`);
                return response.json();
            })
            .then((res)=> {
              if (res === 'not_valid') {
                performLogoutWithoutRedirect(this.$store)
                return;
              }
              if (pageLink == 'information') {
                this.customer_link = pageLink+"?t="+res
              } else if (pageLink == 'summary') {
                this.debit_link = pageLink+"?t="+res
              } else {
                this.debit_link = null;
              }
            })
        } catch (error) {
          // TypeError: Failed to fetch
          console.warn('There was an error to get policy Id', error);
        }
        finally {
          this.processing = false;
        }
      },
    },
  }
  </script>
  <style scoped>
    ::v-deep .dropdown-menu {
      max-height: 250px;
      overflow-y: auto;
    }
    ::v-deep .card-title {
      color: var(--foreground-color);
      font-size: 1.65rem;
    }
    ::v-deep .card_code_table .card-header {
      display: none !important;
    }
    span.cancelled {
        background-color: var(--danger);
    }
    span.issued {
        background-color: var(--accent3-color);
    }
  </style>
  